import {
  Button,
  OptionGroup,
  PanelSection,
  SidePanel,
} from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SearchFilter } from 'common/components/SearchFilter';
import { useTranslation } from 'common/hooks/useTranslation';
import { useBudgetOwnerRole } from 'modules/app/hooks/useBudgetOwnerRole';
import { useUser } from 'modules/app/hooks/useUser';
import { useUserRoles } from 'modules/app/hooks/useUserRoles';
import { useCostCentersQuery } from 'modules/budgets/apis';
import type { CostCenter } from 'modules/budgets/models/costCenter';
import type { Team } from 'modules/budgets/models/team';
import { CustomFieldFilters } from 'modules/custom-fields';
import { unwrapQuery } from 'src/core/api/unwrapQuery';

import { PaymentFiltersForm } from './PaymentFiltersForm';
import {
  type Filters,
  type FiltersPayload,
  convertFiltersToPayload,
  convertPayloadToFilters,
  getActiveFiltersCount,
} from './types';

type Props = {
  users: { id: string; fullname: string }[];
  teams: Team[];
  setPaymentFilters: (filters: FiltersPayload) => void;
  setFilterCount: (count: number) => void;
};

export const PaymentFilters = ({
  users,
  teams,
  setPaymentFilters,
  setFilterCount,
}: Props) => {
  const { t } = useTranslation('global');
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const initialFilters = convertPayloadToFilters(
    Array.from(queryParams.entries()).map(([type, value]) => ({ type, value })),
  );

  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);

  const applyFilters = (filtersToApply: Filters) => {
    const payload = convertFiltersToPayload(filtersToApply);
    setPaymentFilters(payload);
  };

  const countFilters = getActiveFiltersCount(initialFilters);
  setFilterCount(countFilters);

  const costCentersQuery = useCostCentersQuery();
  const costCenters = unwrapQuery(costCentersQuery) ?? [];

  const { isRequester, isAccountOwner, isAdmin, isController } = useUserRoles();
  const isBudgetOwner = useBudgetOwnerRole();
  const user = useUser();

  const shouldDisplayMyPaymentShortcut =
    isRequester && (isAccountOwner || isAdmin || isController || isBudgetOwner);
  const filteringOnCurrentUser =
    initialFilters.requesters?.length === 1 &&
    initialFilters.requesters.every((requester) => requester.key === user.id);

  return (
    <>
      <div className="bg-background-primary-default relative z-[9] my-24 flex gap-8">
        <SearchFilter
          search={initialFilters.textSearch ?? ''}
          setSearch={(newSearch) => {
            const newFilters: Filters = {
              ...initialFilters,
              textSearch: newSearch,
            };
            if (!newFilters.textSearch) {
              delete newFilters.textSearch;
            }
            applyFilters(newFilters);
          }}
        />

        {shouldDisplayMyPaymentShortcut && (
          <OptionGroup
            name="filter-my-payments"
            className="w-[248px]"
            options={[
              {
                value: false,
                label: t('payments.filters.option-all-payments'),
              },
              {
                value: true,
                label: t('payments.filters.option-my-payments'),
              },
            ]}
            value={filteringOnCurrentUser}
            onChange={() => {
              if (filteringOnCurrentUser) {
                applyFilters({
                  ...initialFilters,
                  requesters: [],
                });
              } else {
                applyFilters({
                  ...initialFilters,
                  requesters: [{ key: user.id, label: user.fullname }],
                });
              }
              setIsPanelOpen(false);
            }}
          />
        )}

        <Button
          variant={countFilters > 0 ? 'secondaryBrand' : 'secondaryNeutral'}
          iconName="adjustments-horizontal"
          text={
            t('misc.filter') + (countFilters > 0 ? ` (${countFilters})` : '')
          }
          onClick={() => {
            setIsPanelOpen(true);
          }}
        />
      </div>

      {isPanelOpen && (
        <FiltersPanel
          close={() => {
            setIsPanelOpen(false);
          }}
          applyFilters={applyFilters}
          filters={filters}
          initialFilters={initialFilters}
          setFilters={setFilters}
          users={users}
          teams={teams}
          costCenters={costCenters}
        />
      )}
    </>
  );
};

/**
 * Children components
 */

const FiltersPanel = ({
  close,
  applyFilters,
  initialFilters,
  filters,
  setFilters,
  users,
  teams,
  costCenters,
}: {
  close: () => void;
  applyFilters: (filters: Filters) => void;
  setFilters: (filters: Filters) => void;
  initialFilters: Filters;
  filters: Filters;
  users: { id: string; fullname: string }[];
  teams: Team[];
  costCenters: CostCenter[];
}) => {
  const { t } = useTranslation('global');

  // Reset non-applied filters when the panel is closed
  useEffect(() => {
    setFilters(initialFilters);
  }, []);

  const onSubmit = () => {
    applyFilters(filters);
    close();
  };

  return (
    <SidePanel
      className="z-[calc(var(--grapes-z-index-side-panel)+1)]"
      title={t('misc.selectAndApplyFilters')}
      onClose={() => {
        close();
      }}
      footer={
        <div className="grid w-full grid-cols-2 gap-8">
          <Button
            variant="secondaryNeutral"
            text={t('misc.resetFilters')}
            onClick={() => {
              setFilters({});
              applyFilters({});
              close();
            }}
          />
          <Button text={t('misc.apply')} onClick={onSubmit} />
        </div>
      }
    >
      <PanelSection>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <PaymentFiltersForm
            users={users}
            teams={teams}
            costCenters={costCenters}
            filters={filters}
            setFilters={setFilters}
          />
          <CustomFieldFilters
            eligibleTypes={['payment', 'expense']}
            filters={filters.customFields ?? {}}
            setFilters={(customFields) => {
              const nonNullCustomFields = Object.fromEntries(
                Object.entries(customFields).filter(
                  ([_, value]) => value !== null,
                ),
              );
              if (Object.keys(nonNullCustomFields).length === 0) {
                const { customFields: customFieldsUnused, ...rest } = filters;
                setFilters(rest);
              } else {
                setFilters({
                  ...filters,
                  customFields: nonNullCustomFields,
                });
              }
            }}
            disableSelectUndefined={false}
          />

          <input type="submit" hidden />
        </form>
      </PanelSection>
    </SidePanel>
  );
};
